// Lazily load the dotlottie-player custom element only when a player is
// actually about to render. Importing the package runs a Lit @customElement
// decorator whose side effect is customElements.define('dotlottie-player', ...),
// which also pulls in lottie-web + fflate + Buffer. Loading it eagerly (via a
// global plugin) forced ~500KB of runtime into the main entry chunk and blocked
// hydration on every page. Here we defer the import until a player scrolls near
// the viewport, so pages without a visible Lottie never pay for it.

let loadPromise = null

// Imports and defines the custom element exactly once per page load. A failed
// import (offline, stale chunk mid-deploy, CDN blip) resets loadPromise so a
// later observer can retry, and is swallowed rather than re-thrown: an
// animation that fails to upgrade is non-critical, and the callers are
// fire-and-forget, so re-throwing would only surface an unhandled rejection to
// Sentry for no user benefit.
function ensureDotLottieDefined() {
  if (import.meta.server) return
  if (window.customElements?.get('dotlottie-player')) return
  if (!loadPromise) {
    loadPromise = import('@johanaarstein/dotlottie-player-light').catch(() => {
      loadPromise = null
    })
  }
}

// Call from a component that renders <dotlottie-player>. Pass a template ref to
// the element or to a stable always-rendered wrapper; the runtime loads when
// that element first approaches the viewport. The ref is read once at mount, so
// it must resolve to an element that exists at mount time (a wrapper, or a
// player whose v-if is already true). Returns nothing; the browser upgrades the
// already-rendered custom element once the module defines it.
export function useDotLottie(elRef) {
  if (import.meta.server) return

  onMounted(() => {
    const el = unref(elRef)
    if (!el) return

    // Fallback for the rare browser without IntersectionObserver: just load.
    if (!('IntersectionObserver' in window)) {
      ensureDotLottieDefined()
      return
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect()
          ensureDotLottieDefined()
        }
      },
      // Start loading a bit before the player is on screen so autoplay looks instant.
      { rootMargin: '200px' }
    )
    io.observe(el)

    onBeforeUnmount(() => io.disconnect())
  })
}
